'use client'
import { useRouter, usePathname, useSearchParams, useParams } from 'next/navigation'
import { Locale, BoraTheme } from '@/root/shared-types'
import { useConfig } from '@/root/shared-hooks'
import { i18nConfig } from '@/configs/i18n'

export const useCustomRouter = () => {
  const theme = useConfig().theme as BoraTheme
  const { locales, defaultLocale } = i18nConfig[theme]

  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const params = useParams()

  const locale = locales.includes(params?.locale as Locale) ? (params?.locale as Locale) : defaultLocale
  const queryString = searchParams?.toString() ? `?${searchParams.toString()}` : ''
  const cleanPathname = pathname?.replace(`/${locale}`, '')
  const getPathWithLocale = (newLocale: Locale) => `/${newLocale}${cleanPathname}${queryString}`

  // Backward compatibility: combine params and searchParams into query object
  // This matches the behavior of Next.js Pages Router where both dynamic segments
  // and query strings were accessible via router.query
  const query = {
    ...Object.fromEntries(searchParams?.entries() ?? []),
    ...params,
  }

  // In App Router, params are immediately available, so isReady is always true
  const isReady = true

  return {
    ...router,
    locale,
    locales,
    asPath: pathname + queryString,
    pathname,
    searchParams,
    params,
    query,
    isReady,
    getPathWithLocale,
  }
}
